import { motion, MotionProps, Transition } from 'framer-motion'
import { useEffect, useState } from 'react'
import useMeasure from 'react-use-measure'
import { twMerge } from 'tailwind-merge'

const collapseTransition: Transition = {
  type: 'spring',
  stiffness: 550,
  damping: 45,
  mass: 0.7,
}

type AnimatedCollapseProps = React.ComponentProps<'div'> &
  MotionProps & {
    open: boolean
    containerClassName?: string
    animateOnMount?: boolean
  }

function AnimatedCollapse({
  children,
  className,
  containerClassName,
  open,
  animateOnMount = true,
  ...props
}: AnimatedCollapseProps) {
  // Bounding rects shrink inside a scaling ancestor (dialog enter animations) and never re-measure.
  const [ref, bounds] = useMeasure({ offsetSize: true })
  const [animationReady, setAnimationReady] = useState(animateOnMount)

  useEffect(() => {
    if (animateOnMount || animationReady || !open || bounds.height <= 0) return

    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setAnimationReady(true))
    })

    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [animateOnMount, animationReady, bounds.height, open])

  return (
    <motion.div
      animate={{
        height: open ? (animationReady ? (bounds.height > 0 ? bounds.height : 'auto') : undefined) : 0,
      }}
      transition={collapseTransition}
      className={twMerge('relative overflow-hidden', className)}
      {...props}
    >
      <div ref={ref} className={twMerge('w-full', containerClassName)}>
        {children}
      </div>
    </motion.div>
  )
}

export { AnimatedCollapse }
